import {
  Flex,
  Form,
  Input,
  Radio,
  Typography,
  Button,
  InputNumber,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useEffect } from "react";

const { Text } = Typography;

export interface Option {
  id: number;
  option: string;
}
export interface AssessmentQuestion {
  id: number;
  question: string;
  submitted_answer: string;
  is_multiple_choice_question: boolean;
  is_correct: boolean | null;
  options: Option[];
  correct_answer: string;
  marks?: string;
  total_marks: number;
  obtained_marks: number;
}
interface ReviewFormProps {
  is_attempted: boolean;
  isDeclared: boolean;
  isReviewed: boolean;
  questions: any[];
  handleFormSubmit: (values: any) => void;
}

export const getPrefix = (is_multiple_choice_question: boolean) => {
  return is_multiple_choice_question ? "mcq" : "msq";
};

export default function ReviewForm({
  is_attempted,
  isDeclared,
  isReviewed,
  questions,
  handleFormSubmit,
}: ReviewFormProps) {
  useEffect(() => {
    const initialValues = getInitialFormValues(questions);
    console.log("Setting initial form values:", initialValues);
    form.setFieldsValue(initialValues);
  }, [questions]);
  const [form] = Form.useForm();

  const getInitialFormValues = (questions: AssessmentQuestion[]) => {
    const initialValues: Record<string, any> = {};

    questions.forEach((question) => {
      // Always set the question field regardless of submitted_answer presence
      const namePrefix = getPrefix(question.is_multiple_choice_question);
      if (question.is_multiple_choice_question) {
        // For multiple choice, convert to number
        initialValues[`question-${namePrefix}-${question.id}`] = Number(
          question.submitted_answer
        );
      } else {
        // For text answers
        initialValues[`question-${namePrefix}-${question.id}`] =
          question.submitted_answer;
        initialValues[`marks-${question.id}`] = question.obtained_marks || "";
      }

      // Always set marks field even if empty
    });

    console.log("Generated initial values:", initialValues);
    return initialValues;
  };
  return (
    <Form
      form={form}
      onFinish={(values) => handleFormSubmit(values)}
      className="quiz-question admin"
      layout="vertical"
    >
      {questions.map((question: AssessmentQuestion, index: number) => {
        const {
          id,
          is_correct,
          correct_answer,
          options,
          submitted_answer,
          is_multiple_choice_question,
          total_marks,
        } = question;
        const namePrefix = getPrefix(is_multiple_choice_question);
        const answerIndex = correct_answer ? parseInt(correct_answer) - 1 : -1;

        return (
          <div key={id} style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", marginTop: 0, fontWeight: 500 }}>
              <p className="question-text" style={{ flexGrow: 1 }}>
                {index + 1}. {question.question}
              </p>
              <p>
                {question.total_marks} Mark{question.total_marks > 1 ? "s" : ""}
              </p>
            </div>
            <Form.Item
              style={{ marginBottom: "12px", marginLeft: "15px" }}
              name={`question-${namePrefix}-${id}`}
              rules={[{ required: true, message: "Answer is required" }]}
            >
              {is_multiple_choice_question ? (
                <Radio.Group disabled={is_attempted}>
                  {options.map((option, idx) => (
                    <div style={{ marginBottom: "10px" }} key={idx}>
                      <Radio value={idx + 1}>{option.option}</Radio>
                    </div>
                  ))}
                </Radio.Group>
              ) : (
                <Input.TextArea
                  className="subjective-answer"
                  autoSize={{ minRows: 3 }}
                  style={{
                    background: "#F5F5F5",
                    borderColor: "#D9D9D9",
                  }}
                  readOnly={is_attempted}
                  placeholder="Student's Answer"
                />
              )}
            </Form.Item>

            {/* Show correct/wrong indicator for multiple choice questions */}
            {is_multiple_choice_question &&
              typeof is_correct !== "undefined" && (
                <div
                  style={{
                    marginBottom: "12px",
                    marginLeft: "15px",
                  }}
                >
                  {is_correct ? (
                    <Text className="option-response" type="success">
                      <CheckOutlined /> Correct!
                    </Text>
                  ) : (
                    <Text className="option-response" type="danger">
                      <CloseOutlined /> Wrong!
                    </Text>
                  )}
                </div>
              )}

            {/* Show correct answer for wrong multiple choice answers */}
            {is_multiple_choice_question &&
              typeof is_correct !== "undefined" &&
              !is_correct &&
              answerIndex >= 0 && (
                <div
                  style={{
                    marginBottom: "12px",
                    marginLeft: "15px",
                  }}
                >
                  *Correct answer is{" "}
                  <span>"{options[answerIndex].option}"</span>.
                </div>
              )}

            {/* For subjective questions, allow adding marks */}
            {!is_multiple_choice_question && (
              <Form.Item
                label={`Marks (out of ${total_marks})`}
                style={{
                  marginBottom: "12px",
                  marginLeft: "15px",
                }}
                name={`marks-${id}`}
                rules={[{ required: true, message: "Assign marks" },{
                  validator: (_, value) =>
                    value > total_marks || value < 0
                      ? Promise.reject(new Error(`Marks must range from 0 - ${total_marks}.`))
                      : Promise.resolve(),
                }]}
              >
                <InputNumber disabled={isReviewed}
                onKeyUp={(e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  }} 
  onChange={(value: number | null) => {
    if (value && value > total_marks) {
      return
    }
  }}/>
              </Form.Item>
            )}
          </div>
        );
      })}

      {/* Action buttons */}
      <Flex justify="center" gap="middle" style={{ marginTop: "32px" }}>
        {!isDeclared && (
          <Button
            type="primary"
            color="primary"
            htmlType="submit"
            disabled={isReviewed}
          >
            Submit Review
          </Button>
        )}
      </Flex>
    </Form>
  );
}
